import { SurveyTranslation, Translation } from '@tm/shared-lib/src/i18n'
import { useCallback, useContext, useMemo } from 'react'
import { TranslationsContext } from '../context/translationsContext'

function isRecord(obj: unknown): obj is Record<string, string> {
  if (!obj) return false
  if (typeof obj !== 'object') return false

  if (Array.isArray(obj)) return false

  if (Object.getOwnPropertySymbols(obj).length > 0) return false

  return Object.values(obj).every(value => typeof value === 'string')
}

export type TranslateFunction = (txt: string, defaultValue?: string) => string
export type TranslateObjectFunction = (translations: Translation<string> | string | undefined) => string

export function useI18N(): {
  t: TranslateFunction
  tt: TranslateObjectFunction
  lang: string
  surveyLanguages: SurveyTranslation[]
} {
  const ctx = useContext(TranslationsContext)
  if (!ctx) throw new Error('Translations context not provided')
  const { surveyLanguages = [], lang, useDefault = false, fallbackToMainLang = false } = ctx || {}

  const languages = useMemo(() => {
    return surveyLanguages.filter(e => e.lang === lang)
  }, [surveyLanguages, lang])

  const fallbackLang = useMemo(() => {
    if (useDefault) return '_d'
    return surveyLanguages.find(e => e.main)?.lang || '_d'
  }, [surveyLanguages, useDefault])

  const fallbackTranslations = useMemo(() => {
    return surveyLanguages.find(e => e.main)?.translations || {}
  }, [surveyLanguages, lang])

  const t: TranslateFunction = useCallback(
    (txt, defaultValue?) => {
      if (languages.length === 1) {
        const translations = languages[0].translations
        if (txt in translations) return translations[txt]
        if (fallbackToMainLang && txt in fallbackTranslations) return fallbackTranslations[txt]
      }
      return typeof defaultValue === 'string' ? defaultValue : txt
    },
    [languages, fallbackTranslations, fallbackToMainLang]
  )

  /** Translate a translation object to the active language */
  const tt = useCallback(
    (translations: unknown) => {
      if (typeof translations === 'string') return translations
      if (!isRecord(translations)) return ''
      const activeLang = useDefault ? '_d' : lang
      if (activeLang in translations && translations[activeLang]) return translations[activeLang]
      if (fallbackToMainLang && fallbackLang in translations && translations[fallbackLang])
        return translations[fallbackLang]
      return ''
    },
    [lang, fallbackToMainLang, fallbackLang]
  )

  return { t, tt, lang: useDefault ? '_d' : lang, surveyLanguages }
}

export default useI18N
